import {
  Clock,
  DoorOpen,
  GraduationCap,
  Link as LinkIcon,
  User,
  Users,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AcademiaSelect from '../../common/AcademiaSelect';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAcademiaChargeHoraire } from '../../../hooks/useAcademiaAdmin';

const JOURS = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'] as const;

const JOUR_LABELS: Record<string, string> = {
  LUNDI: 'Lun',
  MARDI: 'Mar',
  MERCREDI: 'Mer',
  JEUDI: 'Jeu',
  VENDREDI: 'Ven',
  SAMEDI: 'Sam',
};

const JOUR_LABELS_LONG: Record<string, string> = {
  LUNDI: 'Lundi',
  MARDI: 'Mardi',
  MERCREDI: 'Mercredi',
  JEUDI: 'Jeudi',
  VENDREDI: 'Vendredi',
  SAMEDI: 'Samedi',
};

type CohorteRow = {
  id: string;
  code: string;
  libelle: string;
  statut: string;
  idFormateur: string | null;
  formateurNom: string | null;
  dateDebut: string;
  dateFin: string;
};

type FiliereBlock = {
  id: string;
  code: string;
  libelle: string;
  cohortes: CohorteRow[];
  cohortesAssignees: number;
  cohortesTotal: number;
};

type GridCell = {
  idVacation: string;
  vacationLibelle: string;
  jourSemaine: string;
  salleCode: string;
  salleLibelle: string;
  capacite: number;
  filiereId: string | null;
  filiereCode: string | null;
  filiereLibelle: string | null;
  cohortesFiliere: CohorteRow[];
  cohortesAssignees: number;
  cohortesTotal: number;
};

type FormateurRow = {
  id: string;
  nomComplet?: string;
  profil?: { prenom?: string; nom?: string };
};

type ToastState = {
  message: string;
  variant: 'success' | 'error';
};

function formateurLabel(f: FormateurRow): string {
  const fromProfil = [f.profil?.prenom, f.profil?.nom].filter(Boolean).join(' ');
  return (f.nomComplet || fromProfil).trim() || 'Formateur';
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ChargeHoraireGridCell({ cell }: { cell: GridCell }) {
  const filiereLabel = cell.filiereLibelle || cell.filiereCode || 'Formation non planifiée';
  const cohortesAvecFormateur = cell.cohortesFiliere.filter((c) => c.formateurNom);
  const formateursUniques = Array.from(
    new Set(cohortesAvecFormateur.map((c) => c.formateurNom).filter(Boolean)),
  ) as string[];

  return (
    <div className="planning-cell-card planning-cell-card--active">
      <div className="planning-filiere-badge" title={filiereLabel}>
        <GraduationCap className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{filiereLabel}</span>
      </div>
      <p className="text-[10px] text-on-surface-variant">
        <DoorOpen className="mr-1 inline h-3 w-3" aria-hidden />
        {cell.salleLibelle}
      </p>

      {cell.cohortesTotal > 0 ? (
        <div className="mt-2 space-y-1.5">
          {formateursUniques.length > 0 ? (
            <div className="space-y-1">
              <span className="planning-field-label">
                <User className="h-3 w-3" aria-hidden />
                Formateur{formateursUniques.length > 1 ? 's' : ''}
              </span>
              {formateursUniques.map((nom) => (
                <p
                  key={nom}
                  className="rounded-md bg-primary/8 px-2 py-1 text-xs font-bold text-primary"
                  title={nom}
                >
                  {nom}
                </p>
              ))}
            </div>
          ) : null}

          <ul className="space-y-1 border-t border-outline-variant/25 pt-1.5">
            {cell.cohortesFiliere.map((cohorte) => (
              <li key={cohorte.id} className="text-[11px] leading-snug">
                <span className="font-semibold text-on-surface">{cohorte.code}</span>
                <span className="text-on-surface-variant"> · </span>
                {cohorte.formateurNom ? (
                  <span className="font-medium text-primary">{cohorte.formateurNom}</span>
                ) : (
                  <span className="italic text-amber-800">formateur à assigner</span>
                )}
              </li>
            ))}
          </ul>

          <p className="text-[10px] font-semibold text-on-surface-variant">
            {cell.cohortesAssignees}/{cell.cohortesTotal} cohorte(s) assignée(s)
          </p>
        </div>
      ) : (
        <p className="mt-1 text-xs text-on-surface-variant">Aucune cohorte ouverte</p>
      )}
    </div>
  );
}

function CohorteFormateurRow({
  cohorte,
  formateurs,
  onAssign,
}: {
  cohorte: CohorteRow;
  formateurs: FormateurRow[];
  onAssign: (idFormateur: string | null) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  return (
    <tr className="border-t border-outline-variant/30">
      <td className="px-3 py-2.5">
        <p className="font-bold text-primary">{cohorte.code}</p>
        <p className="text-xs text-on-surface-variant">{cohorte.libelle}</p>
        <p className="mt-0.5 text-[10px] text-on-surface-variant">
          {formatDateShort(cohorte.dateDebut)} → {formatDateShort(cohorte.dateFin)}
        </p>
      </td>
      <td className="px-3 py-2.5">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
            cohorte.statut === 'OUVERTE' || cohorte.statut === 'EN_COURS'
              ? 'bg-emerald-50 text-emerald-800'
              : 'bg-surface-container-high text-on-surface-variant'
          }`}
        >
          {cohorte.statut}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <AcademiaSelect
          fieldSize="sm"
          className="min-w-[12rem]"
          disabled={busy}
          value={cohorte.idFormateur ?? ''}
          onChange={(e) => {
            const next = e.target.value || null;
            setBusy(true);
            void onAssign(next).finally(() => setBusy(false));
          }}
        >
          <option value="">Formateur à assigner</option>
          {cohorte.idFormateur &&
          !formateurs.some((f) => f.id === cohorte.idFormateur) &&
          cohorte.formateurNom ? (
            <option value={cohorte.idFormateur}>{cohorte.formateurNom}</option>
          ) : null}
          {formateurs.map((f) => (
            <option key={f.id} value={f.id}>
              {formateurLabel(f)}
            </option>
          ))}
        </AcademiaSelect>
      </td>
    </tr>
  );
}

export default function AcademiaChargeHorairePanel() {
  const ch = useAcademiaChargeHoraire();
  useRegisterPortalRefresh(() => void ch.refetch());
  const [toast, setToast] = useState<ToastState | null>(null);

  const cells = (ch.grille?.cells ?? []) as GridCell[];
  const filiereBlocks = (ch.grille?.filiereBlocks ?? []) as FiliereBlock[];
  const formateurs = ch.formateurs as FormateurRow[];
  const stats = ch.grille?.stats as
    | { totalAffectations?: number; cohortesSansFormateur?: number }
    | undefined;

  const vacations = useMemo(() => {
    const vacIds = new Set(cells.map((c) => c.idVacation));
    const fromGrid = (ch.grille?.vacations ?? []) as { id: string; libelle: string; ordre: number }[];
    return fromGrid.filter((v) => vacIds.has(v.id)).sort((a, b) => a.ordre - b.ordre);
  }, [cells, ch.grille]);

  const salles = useMemo(() => {
    const codes = ['SALLE_1', 'SALLE_2', 'SALLE_3'];
    return codes.map((code) => {
      const cell = cells.find((c) => c.salleCode === code);
      return {
        code,
        libelle: cell?.salleLibelle ?? code.replace('_', ' '),
        capacite: cell?.capacite ?? null,
      };
    });
  }, [cells]);

  const gridTemplateColumns = useMemo(
    () => `minmax(9.5rem, 11rem) repeat(${salles.length}, minmax(15rem, 1fr))`,
    [salles.length],
  );

  const visibleBlocks = useMemo(() => {
    if (!ch.selectedFiliereId) return filiereBlocks;
    return filiereBlocks.filter((b) => b.id === ch.selectedFiliereId);
  }, [filiereBlocks, ch.selectedFiliereId]);

  const runAssign = useCallback(
    async (idCohorte: string, idFormateur: string | null) => {
      try {
        await ch.assignFormateurCohorte(idCohorte, idFormateur);
        setToast({
          message: idFormateur
            ? 'Formateur affecté à la cohorte pour tout le parcours.'
            : 'Affectation formateur retirée.',
          variant: 'success',
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Impossible de modifier l’affectation.';
        setToast({ message, variant: 'error' });
      }
    },
    [ch],
  );

  const sideCard = {
    label: 'Cohortes assignées',
    value: ch.isLoading
      ? '…'
      : `${stats?.totalAffectations ?? 0}/${filiereBlocks.reduce((n, b) => n + b.cohortesTotal, 0)}`,
    helper:
      (stats?.cohortesSansFormateur ?? 0) > 0
        ? `${stats?.cohortesSansFormateur} cohorte(s) sans formateur`
        : 'Affectation valable sur tout le parcours',
    sideLinks: [
      { label: 'Planning vacations', to: '/admin/planning', icon: 'calendar_month' },
      { label: 'Formateurs', to: '/admin/formateurs', icon: 'cast_for_education' },
    ],
  };

  return (
    <AdminPageShell
      title="Charge horaire"
      description="Grille des vacations et affectation des formateurs par cohorte pour l’ensemble du parcours de formation (sans date de séance)."
      sideCard={sideCard}
      onRetry={() => void ch.refetch()}
      isLoading={ch.isLoading}
      isError={ch.isError}
      loadingMessage="Chargement de la charge horaire…"
      errorMessage="Impossible de charger la charge horaire."
      primaryAction={
        <Link
          to="/admin/planning"
          className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2 text-sm font-bold text-white backdrop-blur-sm transition hover:bg-white/25"
        >
          <LinkIcon className="h-4 w-4" aria-hidden />
          Planning vacations
        </Link>
      }
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={() => setToast(null)}
      />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="planning-day-rail" role="tablist" aria-label="Jour de la semaine">
          {JOURS.map((jour) => (
            <button
              key={jour}
              type="button"
              role="tab"
              aria-selected={ch.selectedJour === jour}
              onClick={() => ch.setSelectedJour(jour)}
              className={`planning-day-btn ${ch.selectedJour === jour ? 'planning-day-btn--active' : ''}`}
            >
              {JOUR_LABELS[jour] ?? jour}
            </button>
          ))}
        </div>
        <p className="text-sm text-on-surface-variant">
          Vue{' '}
          <span className="font-semibold text-on-surface">
            {JOUR_LABELS_LONG[ch.selectedJour]?.toLowerCase() ?? ch.selectedJour}
          </span>
          {' — '}
          salles et formations (lecture seule)
        </p>
      </div>

      <div className="planning-board mb-8">
        <div className="planning-board-scroll">
          <div className="planning-grid" style={{ gridTemplateColumns }}>
            <div className="planning-corner">
              <Clock className="h-4 w-4 text-primary" aria-hidden />
              Créneaux
            </div>
            {salles.map((s) => (
              <div key={s.code} className="planning-salle-head">
                <div className="flex items-start gap-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <DoorOpen className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="planning-salle-head__title">{s.libelle}</p>
                    {s.capacite != null ? (
                      <span className="planning-salle-head__meta">
                        <Users className="h-3 w-3" aria-hidden />
                        {s.capacite} places max
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

            {vacations.map((vac) => (
              <div key={vac.id} className="contents">
                <div className="planning-vacation-label">
                  <span className="planning-vacation-label__badge">Vacation</span>
                  <span className="planning-vacation-label__time">{vac.libelle}</span>
                </div>
                {salles.map((s) => {
                  const cell = cells.find((c) => c.idVacation === vac.id && c.salleCode === s.code);
                  return (
                    <div
                      key={`${vac.id}-${s.code}`}
                      className={`planning-cell ${cell?.filiereId ? 'planning-cell--occupied' : ''}`}
                    >
                      {cell?.filiereId ? (
                        <ChargeHoraireGridCell cell={cell} />
                      ) : (
                        <div className="planning-cell-card planning-cell-card--free">
                          <p className="text-xs text-on-surface-variant">Créneau libre</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="rounded-2xl border border-outline-variant/40 bg-white p-4 shadow-sm md:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-base font-extrabold text-on-surface">
                Formateurs par cohorte — parcours complet
              </h2>
            </div>
            <p className="mt-1 text-sm text-on-surface-variant">
              Choisissez le formateur responsable de chaque cohorte. L’affectation s’applique à tout le
              parcours et se répercute sur les sessions du planning.
            </p>
          </div>
          <label className="min-w-[14rem] space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
              Filtrer par formation
            </span>
            <AcademiaSelect
              value={ch.selectedFiliereId}
              onChange={(e) => ch.setSelectedFiliereId(e.target.value)}
            >
              <option value="">Toutes les formations</option>
              {filiereBlocks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.libelle}
                </option>
              ))}
            </AcademiaSelect>
          </label>
        </div>

        <div className="space-y-6">
          {visibleBlocks.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Aucune cohorte à affecter.</p>
          ) : (
            visibleBlocks.map((block) => (
              <div key={block.id} className="overflow-hidden rounded-xl border border-outline-variant/35">
                <div className="flex flex-wrap items-center justify-between gap-2 bg-surface-container-low/60 px-4 py-3">
                  <div>
                    <p className="font-extrabold text-primary">
                      {block.code} — {block.libelle}
                    </p>
                    <p className="text-xs text-on-surface-variant">
                      {block.cohortesAssignees}/{block.cohortesTotal} cohorte(s) avec formateur assigné
                    </p>
                  </div>
                </div>
                {block.cohortes.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-on-surface-variant">
                    Aucune cohorte pour cette formation. Créez-en une dans{' '}
                    <Link to="/admin/formations" className="font-semibold text-primary hover:underline">
                      Formations
                    </Link>
                    .
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[32rem] text-left text-sm">
                      <thead className="bg-white text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
                        <tr>
                          <th className="px-3 py-2">Cohorte</th>
                          <th className="px-3 py-2">Statut</th>
                          <th className="px-3 py-2">Formateur (parcours)</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {block.cohortes.map((cohorte) => (
                          <CohorteFormateurRow
                            key={cohorte.id}
                            cohorte={cohorte}
                            formateurs={formateurs}
                            onAssign={(id) => runAssign(cohorte.id, id)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </AdminPageShell>
  );
}
